import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { DashboardWindow } from "./dashboardHelpers";
import { isAllSelected } from "./slicerValues";
import { useIsMobile } from "./useIsMobile";

interface SelectOption {
  value: string;
  label: string;
}

// Below this many options a search box is noise (the whole list is visible at a glance);
// from here up it earns its place. Mirrored in SingleSelectSlicer.
const SEARCH_THRESHOLD = 8;

export interface MultiSelectSlicerProps {
  id: string;
  name: string;
  label: string;
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  targetWindow: DashboardWindow;
}

export const MultiSelectSlicer: React.FC<MultiSelectSlicerProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  targetWindow,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile(targetWindow);

  const selectedSet = useMemo(() => new Set(value), [value]);

  // Type-ahead filter for the desktop popover. Mobile's inline section never sets it, so
  // `filtered` degrades to `options` there and the section behaves as before.
  const [search, setSearch] = useState("");
  const showSearch = options.length >= SEARCH_THRESHOLD;
  const filtered = useMemo(
    () =>
      search
        ? options.filter(o =>
            String(o.label ?? o.value)
              .toLowerCase()
              .includes(search.toLowerCase())
          )
        : options,
    [options, search]
  );

  const displayText = useMemo(() => {
    if (value.length === 0) {
      return "None";
    }
    if (isAllSelected(value, options)) {
      return "All";
    }
    if (value.length === 1) {
      const match = options.find(o => o.value === value[0]);
      return match?.label ?? value[0];
    }
    return `${value.length} selected`;
  }, [value, options]);

  // Collapsed mobile section summary. The Figma shows the selected labels comma-joined and
  // ellipsis-truncated ("John Smith, Barry White, Jimmy...") rather than the desktop trigger's
  // "N selected" count. Mapped over `options` so the order is the list's, not the click order
  // that `value` accumulates.
  const summaryText = useMemo(() => {
    if (value.length === 0) {
      return "None";
    }
    if (options.length > 0 && value.length === options.length) {
      return "All";
    }
    return options
      .filter(o => selectedSet.has(o.value))
      .map(o => o.label)
      .join(", ");
  }, [value.length, options, selectedSet]);

  const openPopover = useCallback(() => {
    if (buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
    setSearch("");
    setIsOpen(true);
  }, []);

  const closePopover = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  // Close on outside click, Escape, scroll, or resize. Desktop only: on mobile this renders as an
  // expandable section inline in the Filters sheet, not a floating popover, so there is nothing to
  // dismiss — an outside tap or a scroll must leave the expanded section alone.
  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) {
        return;
      }
      if (buttonRef.current?.contains(target)) {
        return;
      }
      closePopover();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closePopover();
      }
    };
    // Close when the page (or any outer container) scrolls, since the popover is
    // fixed-positioned against a stale buttonRect. Ignore scrolls originating
    // inside the popover's own list so the user can actually scroll the options.
    const handleScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) {
        return;
      }
      closePopover();
    };
    targetWindow.document.addEventListener("mousedown", handleClickOutside);
    targetWindow.document.addEventListener("keydown", handleEscape);
    // `true` so we catch scrolls in any nested scroll container, not just window.
    targetWindow.addEventListener("scroll", handleScroll, true);
    targetWindow.addEventListener("resize", closePopover);
    return () => {
      targetWindow.document.removeEventListener("mousedown", handleClickOutside);
      targetWindow.document.removeEventListener("keydown", handleEscape);
      targetWindow.removeEventListener("scroll", handleScroll, true);
      targetWindow.removeEventListener("resize", closePopover);
    };
  }, [isOpen, targetWindow, closePopover, isMobile]);

  const toggleOption = useCallback(
    (optionValue: string) => {
      // Emit in the options' order, not click order: defaults are stored in options order and
      // every "is this still the default?" check compares by JSON, so untick-then-retick would
      // otherwise read as an active filter, offer a no-op save, and persist as sticky state.
      const next = new Set(value);
      if (selectedSet.has(optionValue)) {
        next.delete(optionValue);
      } else {
        next.add(optionValue);
      }
      const ordered = options.filter(o => next.has(o.value)).map(o => o.value);
      // Keep any selected value the current options no longer list, rather than dropping it.
      const extra = value.filter(v => next.has(v) && !options.some(o => o.value === v));
      onChange([...ordered, ...extra]);
    },
    [selectedSet, value, options, onChange]
  );

  // "Select all" is scoped to the FILTERED options: while a search query is active it means
  // "select all matches" (and unticking removes just the matches), so search + select-all
  // composes into bulk-picking a subset. With no query `filtered` is `options` and this is
  // the plain select-everything toggle.
  const allSelected = filtered.length > 0 && filtered.every(o => selectedSet.has(o.value));
  const someSelected = !allSelected && filtered.some(o => selectedSet.has(o.value));
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const toggleSelectAll = useCallback(() => {
    const filteredValues = new Set(filtered.map(o => o.value));
    const currentlyAll = filteredValues.size > 0 && filtered.every(o => selectedSet.has(o.value));
    if (currentlyAll) {
      onChange(value.filter(v => !filteredValues.has(v)));
    } else {
      // Emit in the options' order, not click order: the default is stored in options order,
      // so a re-selected "all" compares equal to it instead of reading as an active filter.
      const next = new Set([...value, ...filtered.map(o => o.value)]);
      onChange(options.filter(o => next.has(o.value)).map(o => o.value));
    }
  }, [filtered, selectedSet, value, onChange]);

  // Mobile (<640px): an expandable section inline in the Filters sheet rather than a popover, per
  // the "Filters Open"/"Filters Closed" Figma frames. Collapsed it is a single row of
  // label + selected-values summary + chevron; expanded it reveals "Select All" and the options
  // below a divider. Nothing is portalled or fixed-positioned, so the sheet just grows and scrolls.
  if (isMobile) {
    const listId = `${id}_options`;
    // Sizes are the Figma's exact px rather than the rem scale: the viewer renders in a 14px-root
    // iframe, so `text-sm`/`p-2` would land ~12.5% under spec (12.25px text, a 14px checkbox).
    const optionRowClassName =
      "flex items-center gap-[8px] p-[8px] text-[14px] leading-[18px] font-semibold text-main-foreground cursor-pointer";
    const checkboxClassName =
      "size-[16px] shrink-0 rounded-[2px] accent-primary-button-background cursor-pointer";
    return (
      <div
        className={`w-full rounded-[8px] bg-main-background-secondary px-[12px] ${
          // Collapsed the card itself supplies the 12px above/below the single row; expanded it
          // drops to 8px because the title row and the last option row each add their own 8px.
          isOpen ? "py-[8px]" : "py-[12px]"
        }`}
      >
        <div className="flex flex-col gap-[4px]">
          <button
            id={id}
            ref={buttonRef}
            type="button"
            onClick={() => setIsOpen(v => !v)}
            aria-expanded={isOpen}
            aria-controls={listId}
            className={`flex w-full items-center gap-[12px] text-left cursor-pointer ${
              // The divider separates the title from the option rows, so it is expanded-only.
              isOpen ? "border-b border-main-border py-[8px] pr-[8px]" : ""
            }`}
          >
            <span className="flex-1 min-w-0 text-[14px] leading-[18px] font-bold text-main-foreground truncate">
              {label}
            </span>
            {!isOpen && (
              <span className="flex-1 min-w-0 text-[14px] leading-[18px] font-semibold text-main-muted-foreground text-right truncate">
                {summaryText}
              </span>
            )}
            <span
              className={`material-icons !text-[16px] shrink-0 text-main-foreground transition-transform duration-150 ${
                isOpen ? "rotate-180" : ""
              }`}
            >
              expand_more
            </span>
          </button>
          {isOpen && (
            <div
              id={listId}
              role="group"
              aria-label={label}
              className="flex flex-col gap-[4px] min-w-0"
            >
              {options.length === 0 ? (
                <div className="p-[8px] text-[14px] leading-[18px] text-main-muted-foreground">
                  No options
                </div>
              ) : (
                <>
                  <label className={optionRowClassName}>
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className={checkboxClassName}
                    />
                    <span>Select All</span>
                  </label>
                  {options.map(option => (
                    <label key={option.value} className={optionRowClassName}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(option.value)}
                        onChange={() => toggleOption(option.value)}
                        className={checkboxClassName}
                      />
                      <span className="min-w-0 break-words">{option.label}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  const popover =
    isOpen && buttonRect ? (
      <div
        ref={popoverRef}
        role="listbox"
        aria-multiselectable="true"
        style={{
          position: "fixed",
          top: buttonRect.bottom + 4,
          left: buttonRect.left,
          // Explicit width (not minWidth): a shrink-to-fit popover full of w-full children can
          // stretch to the whole viewport inside the themed iframe. The dropdown hugs its
          // trigger like a native select; long labels truncate.
          width: Math.max(220, buttonRect.width),
          zIndex: 9999,
        }}
        className="max-h-72 flex flex-col rounded-themed border border-card-border bg-card-background text-card-foreground shadow-card overflow-hidden"
      >
        {showSearch && (
          <div className="p-2 border-b border-card-border">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              aria-label="Search options"
              className="w-full px-2 py-1.5 text-sm bg-card-background border border-card-border rounded-themed outline-none text-card-foreground placeholder:text-card-foreground/50"
            />
          </div>
        )}
        <div className="min-h-0 overflow-y-auto themed-menu-scrollbar py-1">
          {options.length === 0 ? (
            <div className="px-4 py-2 text-sm text-card-foreground/50">No options</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-2 text-sm text-card-foreground/50">No matches</div>
          ) : (
            <>
              <label className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium cursor-pointer hover:bg-card-foreground/5 border-b border-card-border">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="cursor-pointer"
                />
                <span>{search ? "Select all matches" : "Select all"}</span>
              </label>
              {filtered.map(option => {
                const checked = selectedSet.has(option.value);
                return (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 px-4 py-1.5 text-sm cursor-pointer hover:bg-card-foreground/5"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOption(option.value)}
                      className="cursor-pointer"
                    />
                    <span className="truncate">{option.label}</span>
                  </label>
                );
              })}
            </>
          )}
        </div>
      </div>
    ) : null;

  return (
    <div className="relative inline-flex items-center">
      <button
        id={id}
        ref={buttonRef}
        type="button"
        onClick={() => (isOpen ? closePopover() : openPopover())}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="themed-slicer-input pr-9 text-left"
      >
        <span className="truncate">
          {label}: {displayText}
        </span>
      </button>
      <span className="absolute right-3 text-card-foreground/70 pointer-events-none">
        <span className="material-icons !text-xl">expand_more</span>
      </span>
      {popover && createPortal(popover, targetWindow.document.body)}
    </div>
  );
};
